// @flow

import React, {Fragment, type Node, type Element} from 'react';
import {connect} from 'react-redux';

import Dropzone from 'react-fine-uploader/dropzone';
import FileInput from 'react-fine-uploader/file-input';

import {
  removeMediaPath,
  getErrorMessage,
  type UploaderOptionsType,
} from 'app/utils/fineuploader';
import FineUploaderFileList from './FineUploaderFileList';
import {showNotification} from 'app/toasts/slice';
import {type StoreShapeType} from 'app/reducers/types';
import {accountSettingsSelector} from 'app/settings/api';

type OwnProps = {|
  uploader?: any,
  awsUploadProfile: 'FILES' | 'IMAGES' | 'DOCIMPORT',
  className?: string,
  multiple?: boolean,
  uploaderFactory: (
    opts: {[string]: any},
    accountId: number,
    mediaURL: string,
    otherOptions?: UploaderOptionsType
  ) => any,
  onUploadComplete: (
    path: string,
    key: string,
    id: number,
    name: string,
    response: any
  ) => any,
  onUploadStart?: (id: number) => any,
  onUploadError?: (id: number) => any,
  onUploadCancel?: (id: number) => any,
  children: (FileInput, any) => Node,
  uploadListRender: (Element<typeof FineUploaderFileList>) => Node,
|};

type StateProps = {|
  accountId: number,
  mediaURL: string,
  s3Upload: {[string]: any},
|};

type DispatchProps = {|
  showNotification: (string, string) => void,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type ReponseType = {|
  success: boolean,
|};

type State = {|
  submittedFiles: Array<number>,
|};

class BaseFineUploaderArea extends React.Component<Props, State> {
  state = {
    submittedFiles: [],
  };

  uploader: any;

  constructor(props: Props) {
    super(props);
    this.uploader = props.uploader || this.buildUploader();
  }

  componentDidMount() {
    this.uploader.on('submitted', this.handleSubmitted);
    this.uploader.on('complete', this.handleComplete);
    this.uploader.on('error', this.handleError);
  }

  componentWillUnmount() {
    this.uploader.off('submitted', this.handleSubmitted);
    this.uploader.off('complete', this.handleComplete);
    this.uploader.off('error', this.handleError);
  }

  handleSubmitted = (id: number) => {
    this.setState({
      submittedFiles: this.state.submittedFiles.concat([id]),
    });
    if (this.props.onUploadStart != null) {
      this.props.onUploadStart(id);
    }
  };

  handleComplete = (id: number, name: string, response: ReponseType) => {
    if (response.success === true) {
      const key = this.uploader.methods.getKey(id);
      this.props.onUploadComplete(
        removeMediaPath(key, this.props.mediaURL),
        key,
        id,
        name,
        response
      );
    }
  };

  handleError = (id: number, name: string, error: string) => {
    const message = getErrorMessage(this.uploader, error);
    this.props.showNotification(message, 'error');
    if (this.props.onUploadError != null) {
      this.props.onUploadError(id);
    }
  };

  cancelFile = (id: number) => {
    this.setState({
      submittedFiles: this.state.submittedFiles.filter(
        (fileId) => fileId !== id
      ),
    });
    if (this.props.onUploadCancel != null) {
      this.props.onUploadCancel(id);
    }
  };

  buildUploader() {
    const {accountId, mediaURL, s3Upload} = this.props;
    return this.props.uploaderFactory(s3Upload, accountId, mediaURL);
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      children,
      onUploadComplete,
      accountId,
      mediaURL,
      s3Upload,
      showNotification,
      uploader,
      uploadListRender,
      uploaderFactory,
      awsUploadProfile,
      onUploadCancel,
      onUploadStart,
      ...other
    } = this.props;
    /* eslint-enable no-unused-vars */

    const uploadsFileList = (
      <FineUploaderFileList
        uploader={this.uploader}
        files={this.state.submittedFiles}
        onCancelFile={this.cancelFile}
      />
    );

    return (
      <Fragment>
        <Dropzone
          dropActiveClassName="active"
          uploader={this.uploader}
          {...other}
        >
          {children(FileInput, this.uploader)}
        </Dropzone>
        {uploadListRender(uploadsFileList)}
      </Fragment>
    );
  }
}

const mapStateToProps = (
  state: StoreShapeType,
  {awsUploadProfile}: OwnProps
): StateProps => {
  const {account_id, MEDIA_URL, s3Upload} = accountSettingsSelector(state).data;
  return {
    accountId: account_id,
    mediaURL: MEDIA_URL,
    // $FlowFixMe
    s3Upload: s3Upload[awsUploadProfile],
  };
};

export const FineUploaderArea = connect<
  Props,
  OwnProps,
  StateProps,
  DispatchProps,
  StoreShapeType,
  _
>(
  // $FlowFixMe - redux-thunk messes up with actions return types
  mapStateToProps,
  // $FlowFixMe
  {showNotification}
)(BaseFineUploaderArea);
